import { useState } from 'react';
import { Filter } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface FilterOption {
    value: string;
    label: string;
}

interface FilterPopoverProps {
    options: FilterOption[];
    value: string;
    onApply: (value: string) => void;
    allOptionLabel?: string;
    showAllOption?: boolean;
    isFiltered?: boolean;
    label?: string;
    placeholder?: string;
    className?: string;
}

export default function FilterPopover({
    options,
    value,
    onApply,
    allOptionLabel = 'Todos',
    showAllOption = true,
    isFiltered,
    label = 'Filtro',
    placeholder = 'Selecione uma opção',
    className,
}: FilterPopoverProps) {
    const [open, setOpen] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    const hasActiveFilter = isFiltered ?? value !== 'all';

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setTempValue(value);
        }
        setOpen(nextOpen);
    };

    const handleApply = () => {
        onApply(tempValue);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className={cn(
                        'relative shrink-0 rounded-lg border-border-strong bg-card text-muted-foreground hover:border-border-strong hover:bg-card hover:text-foreground',
                        className,
                    )}
                    aria-label="Filtros"
                >
                    <Filter className="size-4" />
                    {hasActiveFilter && (
                        <span className="absolute -right-1 -top-1 size-3 rounded-full border-2 border-white bg-blue-500" />
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72">
                <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">{label}</label>
                    <Select value={tempValue} onValueChange={setTempValue}>
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder={placeholder} />
                        </SelectTrigger>
                        <SelectContent>
                            {showAllOption && <SelectItem value="all">{allOptionLabel}</SelectItem>}
                            {options.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button onClick={handleApply} className="w-full">
                        Aplicar
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
